import {
	Buildings,
	DotsThree,
	Info,
	Key,
	MinusCircle,
} from "@phosphor-icons/react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router";
import { toast } from "sonner";
import {
	useAssignMeterToApartment,
	useUnassignMeterFromApartment,
	useUpdateMeterEncryptionKey,
} from "@/api/districts/mutations";
import { useApartments } from "@/api/districts/queries";
import { SetEncryptionKeyDialog } from "@/components/meters/set-encryption-key-dialog";
import { Button } from "@/components/ui/button";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuSeparator,
	DropdownMenuSub,
	DropdownMenuSubContent,
	DropdownMenuSubTrigger,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { isValidLang, type Lang, ROUTE_SLUGS } from "@/config/routes";
import { useDistrict } from "@/contexts/district-context";

interface MeterRowActionsProps {
	meterType: string;
	serialNumber: string;
	isAssigned: boolean;
}

export function MeterRowActions({
	meterType,
	serialNumber,
	isAssigned,
}: MeterRowActionsProps) {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { lang } = useParams<{ lang: string }>();
	const currentLang: Lang = lang && isValidLang(lang) ? lang : "pl";
	const { districtId } = useDistrict();
	const [search, setSearch] = useState("");

	const { data: apartmentsData } = useApartments({
		district_id: districtId ?? undefined,
		page_size: 1000,
	});
	const apartments = apartmentsData?.results ?? [];

	const filtered = useMemo(() => {
		if (!search) return apartments;
		const q = search.toLowerCase();
		return apartments.filter(
			(a) =>
				a.street.toLowerCase().includes(q) ||
				a.apartment_number.toLowerCase().includes(q),
		);
	}, [apartments, search]);

	const assignMutation = useAssignMeterToApartment();
	const unassignMutation = useUnassignMeterFromApartment();
	const encryptionKeyMutation = useUpdateMeterEncryptionKey();
	const [keyDialogOpen, setKeyDialogOpen] = useState(false);

	function handleAssign(apartmentId: number) {
		assignMutation.mutate(
			{ serialNumber, apartment: apartmentId },
			{
				onSuccess: () => toast.success(t("meters.assignSuccess")),
				onError: () => toast.error(t("meters.assignError")),
			},
		);
	}

	function handleUnassign() {
		unassignMutation.mutate(
			{ serialNumber },
			{
				onSuccess: () => toast.success(t("meters.unassignSuccess")),
				onError: () => toast.error(t("meters.unassignError")),
			},
		);
	}

	function handleSetEncryptionKey(key: string) {
		encryptionKeyMutation.mutate(
			{ serialNumber, encryptionKey: key },
			{
				onSuccess: () => {
					toast.success(t("meters.encryptionKeySuccess"));
					setKeyDialogOpen(false);
				},
				onError: () => toast.error(t("meters.encryptionKeyError")),
			},
		);
	}

	return (
		<>
			<DropdownMenu>
				<DropdownMenuTrigger asChild>
					<Button variant="ghost" size="icon" aria-label={t("common.actions")}>
						<DotsThree className="size-4" weight="bold" />
					</Button>
				</DropdownMenuTrigger>
				<DropdownMenuContent align="end">
					<DropdownMenuItem
						onClick={(e) => {
							e.stopPropagation();
							navigate(
								`/${currentLang}/${ROUTE_SLUGS.meters[currentLang]}/${meterType}/${serialNumber}`,
							);
						}}
					>
						<Info className="size-4" />
						{t("meters.goToDetail")}
					</DropdownMenuItem>
					<DropdownMenuSeparator />
					<DropdownMenuSub>
						<DropdownMenuSubTrigger>
							<Buildings className="size-4" />
							{t("meters.assignToApartment")}
						</DropdownMenuSubTrigger>
						<DropdownMenuSubContent className="max-h-72 w-56 overflow-hidden">
							<div className="p-1">
								<Input
									placeholder={t("meters.searchApartment")}
									value={search}
									onChange={(e) => setSearch(e.target.value)}
									className="h-8 text-sm"
									onClick={(e) => e.stopPropagation()}
									onKeyDown={(e) => e.stopPropagation()}
								/>
							</div>
							<div className="max-h-52 overflow-y-auto">
								{filtered.length > 0 ? (
									filtered.map((a) => (
										<DropdownMenuItem
											key={a.id}
											onClick={(e) => {
												e.stopPropagation();
												handleAssign(a.id);
											}}
										>
											{a.street} {a.apartment_number}
										</DropdownMenuItem>
									))
								) : (
									<DropdownMenuItem disabled>
										{t("meters.noApartments")}
									</DropdownMenuItem>
								)}
							</div>
						</DropdownMenuSubContent>
					</DropdownMenuSub>
					{isAssigned && (
						<DropdownMenuItem
							onClick={(e) => {
								e.stopPropagation();
								handleUnassign();
							}}
						>
							<MinusCircle className="size-4" />
							{t("meters.unassignFromApartment")}
						</DropdownMenuItem>
					)}
					<DropdownMenuSeparator />
					<DropdownMenuItem
						onClick={(e) => {
							e.stopPropagation();
							setKeyDialogOpen(true);
						}}
					>
						<Key className="size-4" />
						{t("meters.setEncryptionKey")}
					</DropdownMenuItem>
				</DropdownMenuContent>
			</DropdownMenu>
			<SetEncryptionKeyDialog
				open={keyDialogOpen}
				onOpenChange={setKeyDialogOpen}
				onSubmit={handleSetEncryptionKey}
				pending={encryptionKeyMutation.isPending}
			/>
		</>
	);
}
